import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDragnDrop from 'components/shared/CustomDragnDrop/CustomDragnDrop';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { useImagePreview } from 'customHook/useProfilePicture';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientService } from 'service/client.service';
import { LeadsService } from 'service/leads.service';
import { LocationService } from 'service/location.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearAddLeadDraft, setAddLeadDraft, setOpenAddLeadDrawer } from 'store/slices/leadsSlice';
import zeplinColor from 'theme/Color';

type AddLeadFormProps = {
  onClose: (event: React.KeyboardEvent | React.MouseEvent) => void;
  setDrawerState: (visible: boolean) => void;
  onLeadAdded?: () => void;
  leadType?: string;
  leadTypeId?: string;
  typeDropdowns?: any;
  dropdownsLoading?: boolean;
  dropdownError?: string | null;
};

const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
    },
  },
};

const AddLeadForm = ({
  setDrawerState,
  onLeadAdded,
  leadType,
  leadTypeId,
  typeDropdowns,
}: AddLeadFormProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const {
    fileInputRef,
    previewUrl: avatarPreview,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  } = useImagePreview(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();

  const { addLeadDraft } = useAppSelector((state) => state.leads);
  const { assigneeDropdown } = useAppSelector((state) => state.leads);
  const wait = useRef<any>();
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressOptions, setAddressOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [formValues, setFormValues] = useState<Record<string, string>>(
    addLeadDraft?.formValues ?? {},
  );
  const [assignOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [selectedItems, setSelectedItems] = useState<{
    assignTo: { Name: string; Id: string }[];
    address: { Name: string; Id: string }[];
  }>({
    assignTo: [],
    address: [],
  });

  const [demographicProOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [searchingCaptiv8, setSearchingCaptiv8] = useState(false);
  const [captiv8List, setCaptiv8List] = useState<any[]>([]);

  const [selectedIntegrations, setSelectedIntegrations] = useState<{
    captiv8: { Name: string; Id: string }[];
    demographicPro: { Name: string; Id: string }[];
  }>({
    captiv8: [],
    demographicPro: [],
  });

  const handleGenerateAnalysis = () => {
    dispatch(
      setAddLeadDraft({
        formValues,
        selectedItems,
        selectedIntegrations,
        activeStep,
        completed,
      }),
    );
    dispatch(setOpenAddLeadDrawer(true));
    setDrawerState(false);
    navigate('/leads/new-lead/social-insights');
  };

  const suggestionCaptiv8API = async (value: string) => {
    try {
      setSearchingCaptiv8(true);
      const res = await ClientService.searchCaptiv8(value?.toLocaleLowerCase());
      setCaptiv8List(
        res?.users?.map((val: any) => ({
          Id: val?.captiv8Id,
          Name: val?.name,
          avatar: val?.avatar,
          followers: val?.followers,
        })) || [],
      );
    } catch (err) {
      completeOperation('fetch', 'Failed to search Captiv8 users', true);
    } finally {
      setSearchingCaptiv8(false);
    }
  };

  const handleCaptiv8Search = async (e: React.SyntheticEvent, value: string) => {
    clearTimeout(wait.current);
    if (value) {
      const getSuggestion = setTimeout(() => suggestionCaptiv8API(value), 1000);
      wait.current = getSuggestion;
    }
  };

  const handleCaptiv8Selection = (value: any) => {
    setSelectedIntegrations((prev) => ({ ...prev, captiv8: value ? [value] : [] }));
  };

  const handleDemographicProSelection = (value: any) => {
    setSelectedIntegrations((prev) => ({ ...prev, demographicPro: value ? [value] : [] }));
  };
  // Centralized dropdown options state
  const [dropdownOptions, setDropdownOptions] = useState({
    salutation: [] as string[],
    leadStatus: [] as string[],
    level: [] as string[],
    college: [] as string[],
    sport: [] as string[],
    position: [] as string[],
    class: [] as string[],
    draftEligible: [] as string[],
    redshirt: [] as string[],
    draftProjection: [] as string[],
    espnRanking: [] as string[],
    sports247Rating: [] as string[],
  });

  // Update dropdown options when typeDropdowns changes
  useEffect(() => {
    if (typeDropdowns) {
      setDropdownOptions({
        salutation: typeDropdowns?.salutation || [],
        leadStatus: typeDropdowns?.status || [],
        level: typeDropdowns?.level || [],
        college: typeDropdowns?.college || [],
        sport: typeDropdowns?.sport || [],
        position: typeDropdowns?.position || [],
        class: typeDropdowns?.class || [],
        draftEligible: typeDropdowns?.draft_eligible || [],
        redshirt: typeDropdowns?.redshirt || typeDropdowns?.draft_eligible || [],
        draftProjection: typeDropdowns?.draft_projection || [],
        espnRanking: typeDropdowns?.espn_rank || [],
        sports247Rating: typeDropdowns?.x247_sports_rating || [],
      });
    }
  }, [typeDropdowns]);
  const suggestionAPI = async (value: string) => {
    try {
      setLoadingAddress(true);
      const res = await LocationService.getAddressSuggestion(value?.toLocaleLowerCase());
      setAddressOptions(
        res?.map((val: any) => ({ Id: val?.description, Name: val?.description })) || [],
      );
    } catch (err) {
      // optional: add alert
    } finally {
      setLoadingAddress(false);
    }
  };

  const handleAddressSearch = async (e: React.SyntheticEvent, value: string) => {
    clearTimeout(wait.current);
    const getSuggestion = setTimeout(() => suggestionAPI(value), 1000);
    wait.current = getSuggestion;
  };

  const handleAddressSelection = (value: any) => {
    setSelectedItems((prev) => ({ ...prev, address: value ? [value] : [] }));
    const parts = value?.Name?.split(',') || [];
    setFormValues((prev) => ({
      ...prev,
      street: parts[0]?.trim() || '',
      city: parts[parts.length - 3]?.trim() || '',
      state: parts[parts.length - 2]?.trim() || '',
      country: parts[parts.length - 1]?.trim() || '',
    }));
  };
  const handleValueChange =
    (key: string) =>
    (event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
      setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const handleSelectChange = (key: string) => (event: any) => {
    setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleAssignSelection = (value: any) => {
    setSelectedItems((prev) => ({ ...prev, assignTo: value ? [value] : [] }));
  };

  const handleAttachmentDrop = (files: File[] | FileList | any) => {
    const fileList = Array.isArray(files) ? files : Array.from(files || []);
  };

  const handleNext = () => {
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    setActiveStep((prev) => Math.min(prev + 1, 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleCreateLead = async () => {
    if (!formValues.name?.trim()) return;
    let uploadedUrl: string | null = null;
    setCreateLoading(true);
    if (avatarPreview) {
      const result = await uploadProfilePicture({ key: false });
      uploadedUrl = result === undefined ? null : result;
    }
    const assignee = selectedItems.assignTo[0];
    const { Name, ...assigneeData } = assignee || {};
    const payload = {
      name: formValues.name?.trim(),
      salutation: formValues.salutation || undefined,
      title: formValues.title?.trim(),
      assignedTo: assigneeData || undefined,
      agency: formValues.agency?.trim(),
      sport: formValues.sport?.trim(),
      position: formValues.position?.trim(),
      class: formValues.class?.trim(),
      level: formValues.level?.trim(),
      college: formValues.college?.trim(),
      status: formValues.leadStatus,
      draftEligible: formValues.draftEligible,
      redshirt: formValues.redshirt,
      draftProjection: formValues.draftProjection,
      espnRank: formValues.espnRank,
      sports247Rating: formValues.sports247Rating,
      pffOverallGrade: formValues.pffOverallGrade?.trim(),
      decisionMaker: formValues.decisionMaker?.trim(),
      manager: formValues.manager?.trim(),
      referredBy: formValues.referredBy?.trim(),
      email: formValues.email?.trim(),
      phone: formValues.mobile?.trim(),
      address: selectedItems.address[0]?.Name || undefined,
      street: formValues.street?.trim(),
      city: formValues.city?.trim(),
      state: formValues.state?.trim(),
      zip: formValues.zip?.trim(),
      country: formValues.country?.trim(),
      instagram: formValues.instagram?.trim(),
      twitter: formValues.twitter?.trim(),
      leadTypeId,
      leadTypeName: leadType,
      profileImageUrl: uploadedUrl ?? '',
    };

    try {
      setCreateLoading(true);
      await LeadsService.createLead(payload);
      dispatch(clearAddLeadDraft());
      dispatch(setOpenAddLeadDrawer(false));
      setDrawerState(false);
      onLeadAdded?.();
    } catch (error) {
      completeOperation('create', 'Failed to refresh leads', true);
    } finally {
      setCreateLoading(false);
    }
  };
  const disabled =
    createLoading || !formValues?.name || !selectedItems?.assignTo || !formValues?.leadStatus;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: zeplinColor.Neutral1,
          zIndex: 10,
        }}
      >
        <CustomStepper
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          setCompleted={setCompleted}
          completed={completed}
          steps={['Lead Details', 'Social Insights']}
        />
      </Box>

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Lead Information
          </Typography>

          {/* Profile Image */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '22px',
                border: !avatarPreview ? `1px dashed ${zeplinColor.Neutral9}` : '',
                overflow: 'hidden',
              }}
            >
              <img
                src={avatarPreview ?? '/assets/account-name.svg'}
                alt='profile icon'
                width={avatarPreview ? 80 : 35}
                height={avatarPreview ? 80 : 35}
                style={{ objectFit: 'cover' }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                  Profile photo
                </Typography>
                <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                  JPG or PNG (max 2 MB)
                </Typography>
              </Box>
              <Box>
                <CustomButton
                  disabled={false}
                  onClick={openFileSelection}
                  variant='contained'
                  color={'primary'}
                  buttonVariant='buttonSmall'
                  style={{
                    borderRadius: { xs: '10px', xl: '14px' },
                    padding: '6px 12px',
                    '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={avatarPreview ? 'Change Photo' : 'Add Photo'}
                />
                <input
                  type='file'
                  multiple
                  accept='.jpeg, .jpg, .png, image/jpeg, image/png'
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                {avatarPreview && (
                  <CustomButton
                    disabled={false}
                    variant='contained'
                    buttonVariant='buttonSmall'
                    onClick={clearSelection}
                    text={'Delete Photo'}
                    textStyle={{
                      color: zeplinColor.Error,
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      borderRadius: { xs: '10px', xl: '14px' },
                      marginLeft: '8px',
                      padding: '6px 12px',
                      '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Salutation + Name */}
          <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
            <FormControl sx={{ width: '100%', flex: 1 }}>
              <InputLabel
                id='demo-multiple-name-label'
                size={isXs ? 'small' : 'medium'}
                sx={{
                  '&.Mui-focused': { color: zeplinColor.Neutral9 },
                  fontSize: isXs ? 14 : 16,
                }}
              >
                Salutation
              </InputLabel>
              <Select
                labelId='demo-multiple-name-label'
                id='demo-multiple-name'
                size={isXs ? 'small' : 'medium'}
                value={formValues.salutation || ''}
                onChange={handleSelectChange('salutation')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Salutation'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.salutation.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.salutation === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <CustomTextField
              style={{ flex: 3 }}
              name='Name'
              text={formValues.name || ''}
              form={true}
              label={'Name'}
              type='text'
              onChange={handleValueChange('name')}
            />
          </Box>

          {/* Lead Status + Assign To */}
          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='lead-status-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Lead Status
              </InputLabel>
              <Select
                labelId='lead-status-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.leadStatus || ''}
                onChange={handleSelectChange('leadStatus')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Lead Status'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.leadStatus.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.leadStatus === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomAutocomplete
              required={false}
              options={assigneeDropdown || []}
              getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
              label={'Assign To'}
              handleDeleteItems={() => {}}
              selectedItems={selectedItems}
              handleSelection={handleAssignSelection}
              value={selectedItems.assignTo[0]}
              handleFilter={() => {}}
              placeholder='Search people'
              currentLength={assignOptions.length}
              totalLength={assignOptions.length}
              next={() => {}}
              multiple={false}
            />
          </Box>

          {/* Details */}
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Details
          </Typography>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Level
              </InputLabel>
              <Select
                labelId='level-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.level || ''}
                onChange={handleSelectChange('level')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Level'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.level.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.level === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                College
              </InputLabel>
              <Select
                labelId='college-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.college || ''}
                onChange={handleSelectChange('college')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='College'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.college.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.college === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Draft Eligible
              </InputLabel>
              <Select
                labelId='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.draftEligible || ''}
                onChange={handleSelectChange('draftEligible')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Draft Eligible'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.draftEligible.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.draftEligible === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='redshirt-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Redshirt
              </InputLabel>
              <Select
                labelId='redshirt-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.redshirt || ''}
                onChange={handleSelectChange('redshirt')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Redshirt'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.redshirt.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.redshirt === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Sport
              </InputLabel>
              <Select
                labelId='level-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.sport || ''}
                onChange={handleSelectChange('sport')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Sport'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.sport.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.sport === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Position
              </InputLabel>
              <Select
                labelId='position-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.position || ''}
                onChange={handleSelectChange('position')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Position'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.position.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.position === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Class
              </InputLabel>
              <Select
                labelId='class-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.class || ''}
                onChange={handleSelectChange('class')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Class'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.class.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.class === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Recruiting Information */}
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Recruiting Information
          </Typography>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Draft Projection
              </InputLabel>
              <Select
                labelId='draft-projection-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.draftProjection || ''}
                onChange={handleSelectChange('draftProjection')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Draft Projection'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.draftProjection.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.draftProjection === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomTextField
              style={{ flex: 1 }}
              name='Decision Maker'
              text={formValues.decisionMaker || ''}
              form={true}
              label={'Decision Maker'}
              type='text'
              onChange={handleValueChange('decisionMaker')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='PFF Overall Grade'
              text={formValues.pffOverallGrade || ''}
              form={true}
              label={'PFF Overall Grade'}
              type='text'
              onChange={handleValueChange('pffOverallGrade')}
            />
            <CustomTextField
              style={{ flex: 1 }}
              name='Agency'
              text={formValues.agency || ''}
              form={true}
              label={'Agency'}
              type='text'
              onChange={handleValueChange('agency')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                247 Sports Rating
              </InputLabel>
              <Select
                labelId='247-sports-rating-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.sports247Rating || ''}
                onChange={handleSelectChange('sports247Rating')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='247 Sports Rating'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.sports247Rating.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.sports247Rating === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomTextField
              style={{ flex: 1 }}
              name='Manager'
              text={formValues.manager || ''}
              form={true}
              label={'Manager'}
              type='text'
              onChange={handleValueChange('manager')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                id='draft-eligible-label'
                size={isXs ? 'small' : 'medium'}
                sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
              >
                Espn Rank
              </InputLabel>
              <Select
                labelId='espn-rank-label'
                size={isXs ? 'small' : 'medium'}
                value={formValues.espnRank || ''}
                onChange={handleSelectChange('espnRank')}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='Espn Rank'
                  />
                }
                renderValue={(selected) => {
                  // console.log(selected);
                  return `${selected.slice(0, 1).toLocaleUpperCase()}${selected.slice(1)}`;
                }}
                MenuProps={MenuProps}
                IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} />}
              >
                {dropdownOptions.espnRanking.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={formValues.espnRank === name}
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': { color: zeplinColor.BasicBlack },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomTextField
              style={{ flex: 1 }}
              name='Referred By'
              text={formValues.referredBy || ''}
              form={true}
              label={'Referred By'}
              type='text'
              onChange={handleValueChange('referredBy')}
            />
          </Box>

          {/* Contact Information */}
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Contact Information
          </Typography>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='Mobile'
              text={formValues.mobile || ''}
              form={true}
              label={'Mobile'}
              type='text'
              isPhoneMask
              onChange={handleValueChange('mobile')}
            />
            <CustomTextField
              style={{ flex: 1 }}
              name='Email'
              text={formValues.email || ''}
              form={true}
              label={'Email'}
              type='text'
              onChange={handleValueChange('email')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <Box sx={{ flex: 1 }}>
              <CustomAutocomplete
                loading={loadingAddress}
                required={false}
                options={addressOptions || []}
                getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
                label={'Search Address'}
                handleDeleteItems={() => {}}
                selectedItems={selectedItems}
                handleSelection={handleAddressSelection}
                value={selectedItems.address[0]}
                handleFilter={handleAddressSearch}
                placeholder='Search address'
                currentLength={addressOptions.length}
                totalLength={addressOptions.length}
                next={() => {}}
                multiple={false}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='Street'
              text={formValues.street || ''}
              form={true}
              label={'Street'}
              type='text'
              onChange={handleValueChange('street')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='City'
              text={formValues.city || ''}
              form={true}
              label={'City'}
              type='text'
              onChange={handleValueChange('city')}
            />
            <CustomTextField
              style={{ flex: 1 }}
              name='State/Province'
              text={formValues.state || ''}
              form={true}
              label={'State/Province'}
              type='text'
              onChange={handleValueChange('state')}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='Zip/Postal Code'
              text={formValues.zip || ''}
              form={true}
              label={'Zip/Postal Code'}
              type='text'
              onChange={handleValueChange('zip')}
            />
            <CustomTextField
              style={{ flex: 1 }}
              name='Country'
              text={formValues.country || ''}
              form={true}
              label={'Country'}
              type='text'
              onChange={handleValueChange('country')}
            />
          </Box>

          {/* Social */}
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Social
          </Typography>

          <Box sx={{ display: 'flex', gap: '24px' }}>
            <CustomTextField
              style={{ flex: 1 }}
              name='Instagram'
              text={formValues.instagram || ''}
              form={true}
              label={'Instagram'}
              type='text'
              onChange={handleValueChange('instagram')}
            />
            <CustomTextField
              style={{ flex: 1 }}
              name='Twitter'
              text={formValues.twitter || ''}
              form={true}
              label={'Twitter'}
              type='text'
              onChange={handleValueChange('twitter')}
            />
          </Box>

          {/* Attachments */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Attachments
            </Typography>
            <CustomDragnDrop onDrop={handleAttachmentDrop} />
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            flex: 1,
            padding: '24px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Integrate Data Sources
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              Select the correct Captiv8 and Demographic Pro Client handles to sync real-time
              engagement data.
            </Typography>
          </Box>
          <CustomAutocomplete
            loading={searchingCaptiv8}
            popupIcon='/assets/Search.svg'
            required={false}
            options={captiv8List || []}
            getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
            label={'Captiv8'}
            handleDeleteItems={() => {}}
            selectedItems={selectedIntegrations.captiv8}
            handleSelection={handleCaptiv8Selection}
            value={selectedIntegrations.captiv8[0]}
            handleFilter={handleCaptiv8Search}
            placeholder='Search Captiv8'
            currentLength={captiv8List.length}
            totalLength={captiv8List.length}
            next={() => {}}
            multiple={false}
          />

          <CustomAutocomplete
            required={false}
            options={demographicProOptions || []}
            getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
            label={'Demographic Pro'}
            handleDeleteItems={() => {}}
            selectedItems={{ ...selectedItems, assignTo: selectedIntegrations.demographicPro }}
            handleSelection={handleDemographicProSelection}
            value={selectedIntegrations.demographicPro[0]}
            handleFilter={() => {}}
            placeholder='Search Demographic Pro'
            currentLength={demographicProOptions.length}
            totalLength={demographicProOptions.length}
            next={() => {}}
            multiple={false}
          />
          <Box
            sx={{
              borderRadius: '14px',
              padding: '12px 16px',
              backgroundColor: zeplinColor.Neutral3,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Demographic pro analysis credits
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Total credits: 514
              </Typography>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px',
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Used to data: 16
              </Typography>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px',
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Still available: 498
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          bottom: '0px',
          margin: 'auto',
          zIndex: '100',
          padding: '24px',
          backgroundColor: zeplinColor.Neutral1,
          display: 'flex',
          gap: '16px',
        }}
      >
        {activeStep === 1 && (
          <CustomButton
            disabled={createLoading}
            onClick={handleBack}
            variant='outlined'
            style={{
              borderRadius: '14px',
              height: 56,
              flex: 1,
              border: `1px solid ${zeplinColor.BasicBlack}`,
            }}
            textStyle={{ color: zeplinColor.BasicBlack }}
            text={'Back'}
          />
        )}
        <CustomButton
          disabled={disabled}
          onClick={handleCreateLead}
          variant={activeStep === 1 ? 'outlined' : 'contained'}
          color={'primary'}
          style={{
            borderRadius: '14px',
            height: 56,
            ...(activeStep === 1 ? { flex: 2 } : {}),
            ...(activeStep === 0 ? { width: '100%' } : {}),
            border: activeStep === 1 ? `1px solid ${zeplinColor.BasicBlack}` : 'none',
            backgroundColor: disabled
              ? zeplinColor.Neutral3
              : activeStep === 1
                ? zeplinColor.Neutral1
                : zeplinColor.Brand1,
          }}
          textStyle={{
            color: disabled
              ? zeplinColor.Neutral8
              : activeStep === 1
                ? zeplinColor.BasicBlack
                : zeplinColor.Neutral1,
          }}
          text={createLoading ? 'Creating...' : 'Create Lead'}
        />
        {activeStep === 1 ? (
          <CustomButton
            disabled={disabled}
            onClick={handleGenerateAnalysis}
            variant='contained'
            style={{
              borderRadius: '14px',
              height: 56,
              flex: 3,
              border: `1px solid ${zeplinColor.BasicBlack}`,
              backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
            }}
            textStyle={{
              color: disabled ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
            }}
            text={'Generate Analysis'}
          />
        ) : (
          <CustomButton
            disabled={disabled}
            onClick={handleNext}
            variant='outlined'
            color={'customSecondary'}
            style={{
              borderRadius: '14px',
              height: 56,
              width: '100%',
              border: `1px solid ${zeplinColor.BasicBlack}`,
              backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
            }}
            textStyle={{
              color: disabled ? zeplinColor.Neutral8 : zeplinColor.BasicBlack,
            }}
            text={'Analyze Social Insights'}
          />
        )}
      </Box>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default AddLeadForm;
